import { useMemo, useState } from "react";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO, track } from "@/components/cobrain/tokens";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { useServicesEstimate } from "@/hooks/useServicesEstimate";

/* Services-shaped payback estimator. Pure client-side arithmetic —
   estimates what the CUSTOMER recovers. No HireEva pricing here,
   per the no-prices-on-services rule. */

type Field = { key: "team" | "hours" | "jobValue"; label: string; hint: string; min: number; max: number; step: number; prefix?: string; suffix?: string };

const FIELDS: Field[] = [
  { key: "team", label: "People in the business", hint: "Everyone touching admin, quotes or follow-up", min: 1, max: 30, step: 1 },
  { key: "hours", label: "Hours a week lost to admin & follow-up", hint: "Per person, honest average", min: 1, max: 25, step: 1 },
  { key: "jobValue", label: "Average value of a job or client", hint: "Used to price the leads that slip", min: 100, max: 20000, step: 100, prefix: "$" },
];

const fmt = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

export function PaybackEstimator() {
  const [team, setTeam] = useState(6);
  const [hours, setHours] = useState(7);
  const [jobValue, setJobValue] = useState(1200);
  const { setEstimate } = useServicesEstimate();

  const values = { team, hours, jobValue };
  const setters = { team: setTeam, hours: setHours, jobValue: setJobValue };

  const result = useMemo(() => {
    // Hours we typically take back once the first two workflows are live: 35–60%.
    const weekly = team * hours;
    const lowHours = weekly * 0.35;
    const highHours = weekly * 0.6;
    const monthlyLowHours = lowHours * 4.33;
    const monthlyHighHours = highHours * 4.33;
    // Loaded cost of an admin hour in a small business.
    const HOURLY = 38;
    const timeLow = monthlyLowHours * HOURLY;
    const timeHigh = monthlyHighHours * HOURLY;
    // Recovered work: follow-up that never happened. Conservative — scales with team size.
    const jobsLow = Math.max(0.5, team * 0.15);
    const jobsHigh = Math.max(1, team * 0.4);
    const revLow = jobsLow * jobValue;
    const revHigh = jobsHigh * jobValue;
    return {
      monthlyHours: Math.round(monthlyLowHours),
      monthlyHoursHigh: Math.round(monthlyHighHours),
      low: timeLow + revLow,
      high: timeHigh + revHigh,
    };
  }, [team, hours, jobValue]);

  return (
    <div style={{
      background: PALETTE.card, border: `1px solid ${PALETTE.line}`, borderRadius: 20,
      padding: "clamp(20px, 3vw, 30px)", fontFamily: FONT_BODY,
      boxShadow: "0 26px 60px -40px rgba(0,0,0,0.4)",
    }}>
      <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted }}>
        Rough payback · 30 seconds
      </div>
      <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(24px, 3vw, 34px)", margin: "10px 0 22px", letterSpacing: "-0.01em" }}>
        What's currently leaking out of your week?
      </h3>

      <div className="svc-est-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,320px)", gap: "clamp(20px, 3vw, 36px)", alignItems: "start" }}>
        <div style={{ display: "grid", gap: 20 }}>
          {FIELDS.map((f) => {
            const v = values[f.key];
            return (
              <label key={f.key} style={{ display: "block" }}>
                <span style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 600, color: PALETTE.ink }}>{f.label}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 14, color: PALETTE.amber, fontWeight: 700 }}>
                    {f.prefix || ""}{v.toLocaleString("en-US")}{f.suffix || ""}
                  </span>
                </span>
                <input
                  type="range"
                  min={f.min} max={f.max} step={f.step} value={v}
                  aria-label={f.label}
                  onChange={(e) => setters[f.key](Number(e.target.value))}
                  style={{ width: "100%", marginTop: 10, accentColor: PALETTE.amber, height: 24 }}
                />
                <span style={{ display: "block", fontSize: 12.5, color: PALETTE.muted, marginTop: 2 }}>{f.hint}</span>
              </label>
            );
          })}
        </div>

        <div style={{
          background: PALETTE.dark, color: "#fff", borderRadius: 16,
          padding: "22px 22px 24px", display: "flex", flexDirection: "column", gap: 14,
        }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>
            Estimated monthly upside
          </div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(30px, 4vw, 42px)", lineHeight: 1.05, color: PALETTE.amberSoft }}>
            {fmt(result.low)} – {fmt(result.high)}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: "rgba(255,255,255,0.78)" }}>
            Roughly <strong style={{ color: "#fff" }}>{result.monthlyHours}–{result.monthlyHoursHigh} hours</strong> a month back,
            plus the follow-up work that currently slips.
          </div>
          <button
            type="button"
            onClick={() => {
              track("services_estimator_cta", { team, hours, jobValue, low: Math.round(result.low), high: Math.round(result.high) });
              setEstimate({ team, hours, jobValue, low: Math.round(result.low), high: Math.round(result.high) });
              openCallMeNow("services_estimator");
            }}
            data-svc-cta="solid"
            style={{
              marginTop: 4, minHeight: 48, borderRadius: 999, border: 0, cursor: "pointer",
              background: PALETTE.amber, color: "#fff", fontSize: 15, fontWeight: 600, fontFamily: FONT_BODY,
            }}
          >
            Talk to Us Now →
          </button>
          <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: "rgba(255,255,255,0.5)" }}>
            Your numbers go across with the request, so the engineer opens the call already looking at them.
            An estimate, not a promise — the audit is where we confirm it, including when it isn't there.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PaybackEstimator;
